/**
 * Menu screens.
 *
 * Every screen is a plain function that draws itself and returns nothing: all
 * state lives on the `GameApi` implementation, which keeps navigation and
 * persistence in one place.
 */

import { formatMoney, formatTime, clamp } from '@shared/math.js';
import { CARS, CAR_BY_ID, getCar } from '@shared/cars.js';
import { TRACKS, TRACK_BY_ID } from '@shared/tracks.js';
import { UPGRADES } from '@shared/upgrades.js';
import { computeHandling, handlingToStats, upgradeProgress } from '@shared/upgrades.js';
import { AI_DIFFICULTIES, AI_ORDER } from '@shared/ai.js';
import { ACHIEVEMENTS, type GameSettings } from '@shared/save.js';
import type { RaceModeId } from '@shared/net.js';
import { PALETTE, type ButtonOptions, type Rect, type Ui } from './ui.js';
import { carSprite } from '../render/cars.js';
import type { GameApi } from './state.js';

const MODES: Array<{ id: RaceModeId; name: string; blurb: string }> = [
  { id: 'quick-race', name: 'Quick Race', blurb: 'A short sprint against a grid of AI opponents.' },
  { id: 'championship', name: 'Championship', blurb: 'A longer points-paying event. Consistency wins.' },
  { id: 'time-trial', name: 'Time Trial', blurb: 'Empty track. Chase the perfect lap.' },
  { id: 'practice', name: 'Practice', blurb: 'No opponents, no pressure. Learn the circuit.' },
  { id: 'drift', name: 'Drift Challenge', blurb: 'Score as many style points as you can.' },
  { id: 'elimination', name: 'Elimination', blurb: 'Last place is knocked out on a timer.' },
  { id: 'checkpoint', name: 'Checkpoint Sprint', blurb: 'A frantic short-format sprint.' },
];

const CAR_CLASS_LABEL: Record<string, string> = {
  hatch: 'Hatchback',
  kart: 'Kart',
  tuner: 'Tuner',
  muscle: 'Muscle',
  rally: 'Rally',
  drift: 'Drift',
  super: 'Supercar',
  offroad: 'Off-road',
};

// --------------------------------------------------------------------- shell

interface Shell {
  content: Rect;
  header: Rect;
  /**
   * A reserved band along the bottom of the content area.
   *
   * Every screen action lives in here — `backButton` on the left, any primary
   * action on the right — so two buttons can never be laid out on top of each
   * other the way they were when each screen positioned its own footer row.
   */
  footer: Rect;
}

/** Height of the reserved footer band, in UI scale units. */
const FOOTER_HEIGHT = 52;

/**
 * Minimum content height a screen is laid out for, in UI scale units.
 *
 * When the window is tall enough to give the content this much room, the screen
 * fills it exactly as before and nothing scrolls. On a shorter window the screen
 * keeps this height and the player scrolls, instead of the layout being crushed
 * until controls are drawn off the bottom edge where they cannot be reached.
 */
const MIN_CONTENT_HEIGHT = 520;

function shell(ui: Ui, title: string, subtitle?: string): Shell {
  const ctx = ui.ctx;
  const w = ui.width;
  const h = ui.height;
  ctx.fillStyle = PALETTE.bg;
  ctx.fillRect(0, 0, w, h);

  // Subtle backdrop so the flat panels do not float in a void.
  ctx.save();
  ctx.globalAlpha = 0.08;
  for (let y = 0; y < h; y += 4) {
    ctx.fillStyle = y % 8 === 0 ? '#ffffff' : '#000000';
    ctx.fillRect(0, y, w, 1);
  }
  ctx.restore();

  const headerH = Math.round(64 * ui.scale);
  ui.rect({ x: 0, y: 0, w, h: headerH }, PALETTE.panel, PALETTE.line);
  ui.text('APOGEE', 24 * ui.scale, headerH / 2, { size: 20, weight: '700' });
  ui.text('RACER', 24 * ui.scale + ui.measure('APOGEE ', 20, '700'), headerH / 2, { size: 20, weight: '700', colour: PALETTE.accent });
  ui.text(title.toUpperCase(), 24 * ui.scale + ui.measure('APOGEE RACER', 20, '700') + 26 * ui.scale, headerH / 2, {
    size: 13,
    colour: PALETTE.muted,
  });
  if (subtitle) {
    ui.text(subtitle, w - 24 * ui.scale, headerH / 2, { size: 12, colour: PALETTE.muted, align: 'right' });
  }

  const margin = 24 * ui.scale;
  const footerH = FOOTER_HEIGHT * ui.scale;
  // The visible body: everything between the header and the bottom of the window.
  const body: Rect = { x: margin, y: headerH + margin, w: w - margin * 2, h: h - headerH - margin * 2 };
  const natural = Math.max(body.h, MIN_CONTENT_HEIGHT * ui.scale);
  // Clips drawing to the body and returns the content box in content space.
  const content = ui.beginScroll(body, natural, footerH);
  // The footer is pinned to the bottom of the *visible* body, so it stays put
  // while the content behind it scrolls.
  const footer: Rect = { x: body.x, y: body.y + body.h - footerH, w: body.w, h: footerH };

  return { content, header: { x: 0, y: 0, w, h: headerH }, footer };
}

/** Keyboard page scrolling, wired from the game's nav keys. */
export function applyMenuScroll(ui: Ui, keys: { pageUp: boolean; pageDown: boolean; home: boolean; end: boolean }): void {
  const step = Math.max(40, ui.interactiveBodyHeight * 0.8);
  if (keys.pageDown) ui.scrollBy(step);
  if (keys.pageUp) ui.scrollBy(-step);
  if (keys.home) ui.scrollBy(-ui.scrollMax);
  if (keys.end) ui.scrollBy(ui.scrollMax);
}

/**
 * Paints the opaque strip the pinned footer actions sit on (see `Ui.footerBar`).
 * Safe to call from several helpers per frame — only the first call paints.
 */
function footerBar(ui: Ui, footer: Rect): void {
  ui.footerBar(footer);
}

/**
 * The "back to the main menu" button, pinned to the bottom-left of the footer.
 * Pass the shell's `footer` rect, never `content`.
 */
function backButton(app: GameApi, footer: Rect): void {
  const s = app.ui.scale;
  footerBar(app.ui, footer);
  const h = Math.min(38 * s, footer.h);
  app.ui.button('← Back', { x: footer.x, y: footer.y + (footer.h - h) / 2, w: 110 * s, h }, () => {
    app.audio.sfx('click');
    app.back();
  }, { pinned: true });
}

/**
 * A screen's primary action, pinned to the bottom-right of the footer so it
 * always sits opposite the back button instead of underneath it.
 */
function footerAction(
  app: GameApi,
  footer: Rect,
  label: string,
  onClick: () => void,
  options: ButtonOptions = {},
): void {
  const s = app.ui.scale;
  footerBar(app.ui, footer);
  const w = Math.min(240 * s, footer.w * 0.5);
  const h = Math.min(42 * s, footer.h);
  app.ui.button(label, { x: footer.x + footer.w - w, y: footer.y + (footer.h - h) / 2, w, h }, onClick, {
    variant: 'primary',
    pinned: true,
    ...options,
  });
}

// ------------------------------------------------------------------- login

/**
 * Geometry of the two login fields. Shared with the game controller, which
 * needs it every frame to keep the DOM input overlay in the right place.
 */
export function loginFieldRects(ui: Ui): { userRect: Rect; passRect: Rect } {
  const cardW = Math.min(420 * ui.scale, ui.width - 40);
  const cardH = 340 * ui.scale;
  const cardX = (ui.width - cardW) / 2;
  const cardY = (ui.height - cardH) / 2;
  const fieldW = cardW - 48;
  const fieldX = cardX + 24;
  return {
    userRect: { x: fieldX, y: cardY + 112 * ui.scale, w: fieldW, h: 40 * ui.scale },
    passRect: { x: fieldX, y: cardY + 178 * ui.scale, w: fieldW, h: 40 * ui.scale },
  };
}

export function drawLogin(app: GameApi): void {
  const ui = app.ui;
  const ctx = ui.ctx;
  const w = ui.width;
  const h = ui.height;
  ctx.fillStyle = PALETTE.bg;
  ctx.fillRect(0, 0, w, h);

  // Backdrop: a slow parade of car sprites, drawn from the real sprite system.
  ctx.save();
  ctx.globalAlpha = 0.12;
  const t = performance.now() / 2600;
  CARS.forEach((car, i) => {
    const y = ((i * 90 + t * 40) % (h + 200)) - 100;
    const image = carSprite(car.sprite, car.palettes[0]!);
    const size = 130;
    ctx.drawImage(image, (i % 2 === 0 ? w * 0.06 : w * 0.72), y, size, size);
  });
  ctx.restore();

  const cardW = Math.min(420 * ui.scale, w - 40);
  const cardH = 340 * ui.scale;
  const card: Rect = { x: (w - cardW) / 2, y: (h - cardH) / 2, w: cardW, h: cardH };
  ui.rect(card, PALETTE.panel, PALETTE.line);
  ui.rect({ x: card.x, y: card.y, w: card.w, h: 4 }, PALETTE.accent);

  ui.text('PRIVATE SERVER', card.x + card.w / 2, card.y + 34 * ui.scale, {
    size: 12,
    colour: PALETTE.accent,
    align: 'center',
    weight: '700',
  });
  ui.text('Sign in to drive', card.x + card.w / 2, card.y + 62 * ui.scale, { size: 22, weight: '700', align: 'center' });
  ui.text('Accounts are created by the server administrator.', card.x + card.w / 2, card.y + 86 * ui.scale, {
    size: 11,
    colour: PALETTE.muted,
    align: 'center',
  });

  const fieldW = card.w - 48;
  const fieldX = card.x + 24;
  const { userRect, passRect } = loginFieldRects(ui);

  ui.text('USERNAME', fieldX, userRect.y - 10 * ui.scale, { size: 10, colour: PALETTE.muted });
  ui.text('PASSWORD', fieldX, passRect.y - 10 * ui.scale, { size: 10, colour: PALETTE.muted });

  // The DOM input is positioned over the focused field; the other one is drawn.
  for (const [key, rect, value, isPassword] of [
    ['username', userRect, app.login.username, false],
    ['password', passRect, app.login.password, true],
  ] as const) {
    if (app.loginFocus === key) {
      app.text.position(rect);
      continue;
    }
    const hovered = ui.pointerIn(rect);
    ui.rect(rect, hovered ? '#232833' : PALETTE.panelAlt, hovered ? PALETTE.muted : PALETTE.line);
    const shown = isPassword ? '•'.repeat(value.length) : value;
    ui.text(shown || (key === 'username' ? 'your username' : 'your password'), rect.x + 12, rect.y + rect.h / 2, {
      size: 14,
      colour: value ? PALETTE.ink : '#5b6172',
    });
    if (ui.pointer.pressed && hovered) {
      app.loginFocus = key;
      focusLoginField(app, key, userRect, passRect);
    }
  }

  const buttonRect = { x: fieldX, y: card.y + 238 * ui.scale, w: fieldW, h: 44 * ui.scale };
  ui.button(app.login.busy ? 'Signing in…' : 'Sign in', buttonRect, () => app.signIn(), {
    variant: 'primary',
    disabled: app.login.busy,
  });

  if (app.login.error) {
    ui.text(app.login.error, card.x + card.w / 2, card.y + 296 * ui.scale, {
      size: 12,
      colour: PALETTE.bad,
      align: 'center',
      maxWidth: card.w - 32,
    });
  } else if (app.store.defaultAdminPassword) {
    ui.text('Tip: the admin password is still the shipped default.', card.x + card.w / 2, card.y + 296 * ui.scale, {
      size: 11,
      colour: PALETTE.warn,
      align: 'center',
    });
  }

  ui.text('Click a field to type. Enter submits.', w / 2, card.y + card.h + 20 * ui.scale, {
    size: 11,
    colour: PALETTE.muted,
    align: 'center',
  });
}

/** Positions the DOM input overlay over the requested login field. */
export function focusLoginField(app: GameApi, field: 'username' | 'password', userRect: Rect, passRect: Rect): void {
  const rect = field === 'username' ? userRect : passRect;
  app.text.focus(rect, {
    value: field === 'username' ? app.login.username : app.login.password,
    placeholder: field === 'username' ? 'your username' : 'your password',
    password: field === 'password',
    maxLength: 64,
    onChange: (value) => {
      if (field === 'username') app.login.username = value;
      else app.login.password = value;
    },
    onSubmit: () => {
      if (field === 'username') {
        app.loginFocus = 'password';
      } else {
        app.signIn();
      }
    },
  });
}

// --------------------------------------------------------------- main menu

export function drawMainMenu(app: GameApi): void {
  const ui = app.ui;
  const { content } = shell(ui, 'Main Menu', `${app.store.user?.displayName ?? ''} · ${formatMoney(app.store.money)}`);

  const s = ui.scale;
  const gap = 16 * s;
  // Two cards side by side when there is width for them, otherwise stacked.
  const cols = content.w > 900 * s ? 2 : 1;
  const stacked = cols === 1;
  const cardW = cols === 2 ? (content.w - gap * 2) / 2 : content.w;
  /**
   * Everything that competes for vertical space below the two cards: the gap
   * between them (stacked), the admin strip when present, the status line, the
   * sign-out button and the shell's footer band. Under-reserving here is what
   * pushed "Sign out" past the bottom of the content box on a short window,
   * where no amount of scrolling could bring it back.
   */
  const belowGap = 16 + (app.store.user?.isAdmin ? 60 : 12) + 16;
  const extras = (FOOTER_HEIGHT + belowGap + 8) * s;
  const cardH = stacked
    ? clamp((content.h - gap - extras) / 2, 150 * s, 240 * s)
    : 240 * s;
  const playCard: Rect = { x: content.x, y: content.y, w: cardW, h: cardH };
  const menuCard: Rect = {
    x: stacked ? content.x : content.x + cardW + gap,
    y: stacked ? content.y + cardH + gap : content.y,
    w: cardW,
    h: cardH,
  };
  const belowCards = Math.max(playCard.y + playCard.h, menuCard.y + menuCard.h) + 16 * s;
  const labelX = playCard.x + 16 * s;

  // ------------------------------------------------------------- play card
  ui.panel(playCard, 'Race');
  const carId = app.store.save.selectedCar;
  const car = getCar(carId);
  const track = TRACK_BY_ID[app.setup.trackId] ?? TRACKS[0]!;
  const image = carSprite(car.sprite, app.store.save.paint[carId] ?? car.palettes[0]!);
  ui.ctx.imageSmoothingEnabled = false;
  ui.ctx.drawImage(
    image,
    playCard.x + playCard.w - 150 * s,
    playCard.y + 46 * s,
    Math.min(120 * s, playCard.h - 92 * s),
    Math.min(120 * s, playCard.h - 92 * s),
  );

  ui.text(car.name, labelX, playCard.y + 56 * s, { size: 17, weight: '700' });
  ui.text(CAR_CLASS_LABEL[car.carClass] ?? car.carClass, labelX, playCard.y + 76 * s, {
    size: 11,
    colour: PALETTE.muted,
  });
  const best = app.store.save.bestLaps[track.id] ?? 0;
  // The detail lines only appear when the card is tall enough to hold them;
  // in the stacked (short) layout they would run into the buttons.
  if (playCard.h >= 200 * s) {
    ui.text(track.name, labelX, playCard.y + 100 * s, { size: 13, colour: PALETTE.accent });
    ui.text(`${track.environment} · ${app.setup.laps} laps · ${app.setup.aiCount} opponents`, labelX, playCard.y + 118 * s, {
      size: 11,
      colour: PALETTE.muted,
    });
    ui.text(`Difficulty: ${AI_DIFFICULTIES[app.setup.difficulty].name}`, labelX, playCard.y + 136 * s, {
      size: 11,
      colour: PALETTE.muted,
    });
    ui.text(`Your best lap: ${best > 0 ? formatTime(best) : 'not set'}`, labelX, playCard.y + 156 * s, {
      size: 11,
      colour: best > 0 ? PALETTE.good : PALETTE.muted,
    });
  }

  const btnH = Math.min(42 * s, playCard.h - 30 * s);
  const btnY = playCard.y + playCard.h - btnH - 12 * s;
  ui.button('Quick race', { x: labelX, y: btnY, w: 150 * s, h: btnH }, () => {
    app.audio.sfx('click');
    app.beginRace();
  }, { variant: 'primary' });
  ui.button('Race setup', { x: labelX + 160 * s, y: btnY, w: 130 * s, h: btnH }, () => {
    app.audio.sfx('click');
    app.show('play');
  });

  // ------------------------------------------------------------ menu column
  ui.panel(menuCard, 'Garage & progress');
  const level = app.store.level;
  ui.text(`Level ${level}`, menuCard.x + 16 * s, menuCard.y + 56 * s, { size: 16, weight: '700' });
  ui.text(`${formatMoney(app.store.money)}`, menuCard.x + menuCard.w - 16 * s, menuCard.y + 56 * s, {
    size: 16,
    weight: '700',
    colour: PALETTE.accent,
    align: 'right',
  });
  ui.text(`${app.store.save.cars.length} cars owned · ${Math.round(upgradeProgress(app.store.save.upgrades[carId] ?? {}) * 100)}% tuned`, menuCard.x + 16 * s, menuCard.y + 78 * s, {
    size: 11,
    colour: PALETTE.muted,
  });

  const buttonRows: Array<[string, () => void]> = [
    ['Garage', () => app.show('garage')],
    ['Upgrades', () => app.show('upgrades')],
    ['Tracks & records', () => app.show('tracks')],
    ['Statistics', () => app.show('stats')],
    ['Multiplayer', () => app.show('multiplayer')],
    ['Settings', () => app.show('settings')],
  ];
  // The three button rows take whatever height the card has left, so they stay
  // inside it when the card is shortened for the stacked layout.
  const rowH = clamp((menuCard.h - 108 * s) / 3, 30 * s, 48 * s);
  const menuBtnH = Math.min(40 * s, rowH - 6 * s);
  buttonRows.forEach(([label, action], i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    ui.button(label, {
      x: menuCard.x + 16 * s + col * ((menuCard.w - 44 * s) / 2 + 12 * s),
      y: menuCard.y + 96 * s + row * rowH,
      w: (menuCard.w - 44 * s) / 2,
      h: menuBtnH,
    }, () => {
      app.audio.sfx('click');
      action();
    });
  });

  if (app.store.user?.isAdmin) {
    const adminY = belowCards;
    ui.rect({ x: content.x, y: adminY, w: content.w, h: 44 * s }, PALETTE.panel, PALETTE.line);
    ui.text('Administrator', content.x + 16 * s, adminY + 22 * s, { size: 12, colour: PALETTE.accent, weight: '700' });
    ui.text('Create accounts and edit any player save file from the admin console.', content.x + 130 * s, adminY + 22 * s, {
      size: 11,
      colour: PALETTE.muted,
      maxWidth: content.w - 380 * s,
    });
    ui.button('Open admin console', { x: content.x + content.w - 210 * s, y: adminY + 6 * s, w: 194 * s, h: 32 * s }, () => {
      window.open('/admin/', '_blank', 'noopener');
    }, { small: true });
  }

  // -------------------------------------------------------------- MOTD strip
  const motdY = belowCards + (app.store.user?.isAdmin ? 60 : 12) * s;
  ui.text(
    app.store.offline ? 'Offline — progress will not be saved to the server.' : `Server: ${app.store.motd || 'online'}`,
    content.x,
    motdY,
    { size: 11, colour: app.store.offline ? PALETTE.warn : PALETTE.muted },
  );
  ui.button('Sign out', { x: content.x + content.w - 110 * s, y: motdY - 16 * s, w: 110 * s, h: 32 * s }, () => app.signOut(), {
    small: true,
    variant: 'ghost',
  });
}

// -------------------------------------------------------------------- play

export function drawRaceSetup(app: GameApi): void {
  const ui = app.ui;
  const s = ui.scale;
  const { content, footer } = shell(ui, 'Race setup', 'Choose a mode, circuit and grid');
  const gap = 14 * s;

  // Three columns (modes | parameters | circuits) rather than two. Race setup
  // carries more controls than any other screen, and with two columns the
  // parameter block was pushed past the bottom of the viewport at every window
  // size. Columns are weighted because the sliders need more room than a name.
  const usableW = content.w - gap * 2;
  const modeW = Math.round(usableW * 0.3);
  const paramW = Math.round(usableW * 0.38);
  const trackW = usableW - modeW - paramW;
  const colH = content.h - footer.h - 4 * s;
  const modeCol: Rect = { x: content.x, y: content.y, w: modeW, h: colH };
  const paramCol: Rect = { x: content.x + modeW + gap, y: content.y, w: paramW, h: colH };
  const trackCol: Rect = { x: content.x + modeW + paramW + gap * 2, y: content.y, w: trackW, h: colH };

  // ------------------------------------------------------------------ modes
  ui.panel(modeCol, 'Mode');
  const rowH = 44 * s;
  let y = modeCol.y + 42 * s;
  for (const mode of MODES) {
    const rect = { x: modeCol.x + 10 * s, y, w: modeCol.w - 20 * s, h: rowH - 5 * s };
    ui.row(rect, { selected: app.setup.mode === mode.id, onClick: () => { app.setup.mode = mode.id; } });
    ui.text(mode.name, rect.x + 10 * s, rect.y + rect.h / 2, { size: 12, weight: '700', maxWidth: rect.w - 20 * s });
    y += rowH;
  }
  // The blurb goes once, below the list: the column is too narrow to show one
  // per row without it wrapping into the following row.
  const selectedMode = MODES.find((m) => m.id === app.setup.mode);
  if (selectedMode && y + 20 * s < modeCol.y + modeCol.h) {
    ui.divider(modeCol.x + 10 * s, y + 6 * s, modeCol.x + modeCol.w - 10 * s);
    ui.wrap(selectedMode.blurb, modeCol.w - 24 * s, 10).slice(0, 3).forEach((line, i) => {
      ui.text(line, modeCol.x + 12 * s, y + 22 * s + i * 14 * s, { size: 10, colour: PALETTE.muted });
    });
  }

  // ------------------------------------------------------------- parameters
  ui.panel(paramCol, 'Parameters');
  const rowX = paramCol.x + 12 * s;
  const rowW = paramCol.w - 24 * s;
  const colBottom = paramCol.y + paramCol.h;
  let py = paramCol.y + 44 * s;
  const sliderH = 34 * s;
  const sliderStep = 38 * s;
  app.setup.laps = ui.slider('Laps', { x: rowX, y: py, w: rowW, h: sliderH }, app.setup.laps, 1, 20, 1, (v) => String(Math.round(v)));
  py += sliderStep;
  app.setup.aiCount = ui.slider('Opponents', { x: rowX, y: py, w: rowW, h: sliderH }, app.setup.aiCount, 0, 8, 1, (v) => String(Math.round(v)));
  py += sliderStep;
  app.setup.eliminationInterval = ui.slider('Knockout', { x: rowX, y: py, w: rowW, h: sliderH }, app.setup.eliminationInterval, 10, 90, 5, (v) => `${Math.round(v)}s`);
  py += sliderStep;
  app.setup.checkpointTarget = ui.slider('Gates', { x: rowX, y: py, w: rowW, h: sliderH }, app.setup.checkpointTarget, 2, 20, 1, (v) => String(Math.round(v)));
  py += 42 * s;

  if (py + 14 * s < colBottom) {
    ui.text('AI difficulty', rowX, py, { size: 11, colour: PALETTE.muted });
    py += 14 * s;
    // Two rows of three so the difficulty names are shown in full rather than
    // being clipped to seven characters to fit a single crowded row.
    const perRow = 3;
    const tabGap = 6 * s;
    const tabW = (rowW - tabGap * (perRow - 1)) / perRow;
    const tabH = 30 * s;
    AI_ORDER.forEach((id, i) => {
      const rect = {
        x: rowX + (i % perRow) * (tabW + tabGap),
        y: py + Math.floor(i / perRow) * (tabH + 4 * s),
        w: tabW,
        h: tabH,
      };
      ui.chip(AI_DIFFICULTIES[id].name, rect, app.setup.difficulty === id, () => {
        app.setup.difficulty = id;
      });
    });
    py += tabH * 2 + 4 * s + 10 * s;

    const blurbY = py;
    if (blurbY < colBottom) {
      ui.wrap(AI_DIFFICULTIES[app.setup.difficulty].blurb, rowW, 10).slice(0, 3).forEach((line, i) => {
        ui.text(line, rowX, blurbY + i * 14 * s, { size: 10, colour: PALETTE.muted });
      });
    }
  }

  // ---------------------------------------------------------------- circuits
  ui.panel(trackCol, 'Circuit');
  const trackRowH = 46 * s;
  let ty = trackCol.y + 42 * s;
  for (const track of TRACKS) {
    if (ty + trackRowH > trackCol.y + trackCol.h) break;
    const rect = { x: trackCol.x + 10 * s, y: ty, w: trackCol.w - 20 * s, h: trackRowH - 4 * s };
    ui.row(rect, { selected: app.setup.trackId === track.id, onClick: () => { app.setup.trackId = track.id; } });
    ui.text(track.name, rect.x + 10 * s, rect.y + 14 * s, { size: 12, weight: '700', maxWidth: rect.w - 20 * s });
    ui.text(`${track.environment} · ${track.defaultLaps} laps`, rect.x + 10 * s, rect.y + 28 * s, {
      size: 10,
      colour: PALETTE.muted,
      maxWidth: rect.w - 90 * s,
    });
    for (let i = 0; i < 5; i++) {
      const filled = i < track.difficulty;
      ui.ctx.fillStyle = filled ? (track.difficulty >= 4 ? PALETTE.bad : PALETTE.accent) : '#2b303d';
      ui.ctx.fillRect(rect.x + rect.w - 70 * s + i * 11 * s, rect.y + 13 * s, 8 * s, 8 * s);
    }
    ty += trackRowH;
  }

  footerAction(app, footer, 'Start race', () => {
    app.audio.sfx('click');
    app.beginRace();
  });
  backButton(app, footer);
}

// ------------------------------------------------------------------ garage

export function drawGarage(app: GameApi): void {
  const ui = app.ui;
  const { content, footer } = shell(ui, 'Garage', `${formatMoney(app.store.money)} available`);
  const gap = 14 * ui.scale;
  const listW = Math.min(content.w * 0.34, 380 * ui.scale);
  const listRect: Rect = { x: content.x, y: content.y, w: listW, h: content.h - 56 * ui.scale };
  const detailRect: Rect = { x: content.x + listW + gap, y: content.y, w: content.w - listW - gap, h: content.h - 56 * ui.scale };

  ui.panel(listRect, 'Showroom');
  const rowH = 44 * ui.scale;
  let y = listRect.y + 44 * ui.scale;
  CARS.forEach((car, index) => {
    if (y + rowH > listRect.y + listRect.h) return;
    const owned = app.store.save.cars.includes(car.id);
    const selected = app.store.save.selectedCar === car.id;
    const rect = { x: listRect.x + 10 * ui.scale, y, w: listRect.w - 20 * ui.scale, h: rowH - 4 * ui.scale };
    ui.row(rect, { selected: selected, onClick: () => { app.garageIndex = index; app.purchaseError = ''; } });
    ui.text(car.name, rect.x + 10 * ui.scale, rect.y + 14 * ui.scale, { size: 13, weight: '700' });
    ui.text(CAR_CLASS_LABEL[car.carClass] ?? car.carClass, rect.x + 10 * ui.scale, rect.y + 29 * ui.scale, {
      size: 10,
      colour: PALETTE.muted,
    });
    ui.text(owned ? (selected ? 'ACTIVE' : 'OWNED') : formatMoney(car.price), rect.x + rect.w - 10 * ui.scale, rect.y + 19 * ui.scale, {
      size: 11,
      align: 'right',
      colour: owned ? (selected ? PALETTE.accent : PALETTE.good) : PALETTE.muted,
    });
    y += rowH;
  });

  // ---------------------------------------------------------------- detail
  const car = CARS[clamp(app.garageIndex, 0, CARS.length - 1)]!;
  const owned = app.store.save.cars.includes(car.id);
  const upgrades = app.store.save.upgrades[car.id] ?? {};
  const tuned = computeHandling(car, upgrades);
  const stats = handlingToStats(tuned);
  const colour = app.store.save.paint[car.id] ?? car.palettes[0]!;

  ui.panel(detailRect, car.name);
  const image = carSprite(car.sprite, colour);
  ui.ctx.imageSmoothingEnabled = false;
  ui.ctx.drawImage(image, detailRect.x + detailRect.w - 190 * ui.scale, detailRect.y + 52 * ui.scale, 170 * ui.scale, 170 * ui.scale);

  const infoX = detailRect.x + 16 * ui.scale;
  ui.text(CAR_CLASS_LABEL[car.carClass] ?? car.carClass, infoX, detailRect.y + 56 * ui.scale, { size: 12, colour: PALETTE.accent });
  const lines = ui.wrap(car.description, detailRect.w - 220 * ui.scale, 12);
  lines.slice(0, 4).forEach((line, i) => {
    ui.text(line, infoX, detailRect.y + 76 * ui.scale + i * 17 * ui.scale, { size: 12, colour: PALETTE.muted });
  });

  const statY = detailRect.y + 156 * ui.scale;
  const statRows: Array<[string, number]> = [
    ['Acceleration', stats.accel],
    ['Top speed', stats.topSpeed],
    ['Handling', stats.handling],
    ['Grip', stats.grip],
    ['Braking', stats.braking],
    ['Weight', stats.weight],
  ];
  statRows.forEach(([label, value], i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    ui.statRow(
      label,
      value,
      {
        x: detailRect.x + 16 * ui.scale + col * ((detailRect.w - 32 * ui.scale) / 2 + 8 * ui.scale),
        y: statY + row * 26 * ui.scale,
        w: (detailRect.w - 32 * ui.scale) / 2,
        h: 20 * ui.scale,
      },
    );
  });

  // ------------------------------------------------------------ paint + buy
  const footerY = detailRect.y + detailRect.h - 96 * ui.scale;
  ui.divider(detailRect.x + 12 * ui.scale, footerY - 10 * ui.scale, detailRect.x + detailRect.w - 12 * ui.scale);
  ui.text('PAINT', detailRect.x + 16 * ui.scale, footerY + 6 * ui.scale, { size: 10, colour: PALETTE.muted });
  car.palettes.forEach((palette, i) => {
    const sw = 26 * ui.scale;
    const rect = { x: detailRect.x + 16 * ui.scale + i * (sw + 6 * ui.scale), y: footerY + 18 * ui.scale, w: sw, h: sw };
    const active = palette.toLowerCase() === colour.toLowerCase();
    ui.rect(rect, palette, active ? PALETTE.ink : PALETTE.line, active ? 2 : 1);
    if (ui.pointerIn(rect) && ui.pointer.pressed) {
      if (owned) app.store.setPaint(car.id, palette);
      app.audio.sfx('click');
    }
  });

  const actionY = footerY + 54 * ui.scale;
  if (owned) {
    ui.button('Set as active car', { x: detailRect.x + 16 * ui.scale, y: actionY, w: 190 * ui.scale, h: 38 * ui.scale }, () => {
      app.store.selectCar(car.id);
      app.audio.sfx('click');
      app.toast(`${car.name} is now your active car.`);
    }, { variant: app.store.save.selectedCar === car.id ? 'ghost' : 'primary' });
    ui.button('Upgrade this car', { x: detailRect.x + 216 * ui.scale, y: actionY, w: 170 * ui.scale, h: 38 * ui.scale }, () => {
      app.upgradeIndex = UPGRADES.findIndex((u) => u.id === 'engine');
      app.show('upgrades');
    });
  } else {
    const affordable = app.store.money >= car.price;
    const gated = !!car.requires && !app.store.save.cars.includes(car.requires);
    ui.button(gated ? 'Locked' : `Buy for ${formatMoney(car.price)}`, {
      x: detailRect.x + 16 * ui.scale,
      y: actionY,
      w: 240 * ui.scale,
      h: 38 * ui.scale,
    }, () => app.buySelectedCar(), { variant: 'primary', disabled: !affordable || gated || app.purchaseBusy });

    const hint = gated
      ? (car.unlockNote ?? `Requires the ${CAR_BY_ID[car.requires!]?.name ?? car.requires}`)
      : affordable
        ? ''
        : `You need ${formatMoney(car.price - app.store.money)} more.`;
    if (hint) {
      ui.text(hint, detailRect.x + 268 * ui.scale, actionY + 19 * ui.scale, { size: 11, colour: PALETTE.warn });
    }
  }
  if (app.purchaseError) {
    ui.text(app.purchaseError, detailRect.x + 16 * ui.scale, actionY + 46 * ui.scale, { size: 11, colour: PALETTE.bad });
  }

  backButton(app, footer);
}

// ---------------------------------------------------------------- upgrades

export function drawUpgrades(app: GameApi): void {
  const ui = app.ui;
  const { content, footer } = shell(ui, 'Upgrades', `${formatMoney(app.store.money)} available`);
  const car = getCar(app.store.save.selectedCar);
  const owned = app.store.save.upgrades[car.id] ?? {};

  const header: Rect = { x: content.x, y: content.y, w: content.w, h: 62 * ui.scale };
  ui.panel(header, car.name);
  ui.text('Every upgrade changes the physics the simulator runs — not just the numbers.', content.x + 16 * ui.scale, header.y + 46 * ui.scale, {
    size: 11,
    colour: PALETTE.muted,
  });

  const listRect: Rect = { x: content.x, y: content.y + 74 * ui.scale, w: content.w, h: content.h - 74 * ui.scale - 56 * ui.scale };
  const cols = 3;
  const gap = 12 * ui.scale;
  const cardW = (listRect.w - gap * (cols - 1)) / cols;
  const cardH = 128 * ui.scale;

  UPGRADES.forEach((def, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const rect: Rect = {
      x: listRect.x + col * (cardW + gap),
      y: listRect.y + row * (cardH + gap),
      w: cardW,
      h: cardH,
    };
    if (rect.y + rect.h > listRect.y + listRect.h) return;
    const level = owned[def.id] ?? 0;
    const maxed = level >= def.levels.length;
    const price = maxed ? 0 : def.levels[level]!.cost;
    const affordable = !maxed && app.store.money >= price;

    ui.rect(rect, PALETTE.panel, PALETTE.line);
    ui.rect({ x: rect.x, y: rect.y, w: rect.w, h: 3 }, maxed ? PALETTE.good : PALETTE.accent);
    ui.text(def.name, rect.x + 12 * ui.scale, rect.y + 22 * ui.scale, { size: 14, weight: '700' });
    ui.text(`${level}/${def.levels.length}`, rect.x + rect.w - 12 * ui.scale, rect.y + 22 * ui.scale, {
      size: 12,
      colour: maxed ? PALETTE.good : PALETTE.muted,
      align: 'right',
    });
    const blurb = ui.wrap(def.description, rect.w - 24 * ui.scale, 10);
    blurb.slice(0, 2).forEach((line, li) => {
      ui.text(line, rect.x + 12 * ui.scale, rect.y + 40 * ui.scale + li * 14 * ui.scale, { size: 10, colour: PALETTE.muted });
    });

    // Level pips.
    def.levels.forEach((_, li) => {
      const pipW = (rect.w - 24 * ui.scale - (def.levels.length - 1) * 3) / def.levels.length;
      ui.rect(
        {
          x: rect.x + 12 * ui.scale + li * (pipW + 3),
          y: rect.y + 70 * ui.scale,
          w: pipW,
          h: 8 * ui.scale,
        },
        li < level ? PALETTE.accent : '#2b303d',
      );
    });

    ui.text(maxed ? 'Fully installed' : (level > 0 ? def.levels[level]!.note : def.tradeoff), rect.x + 12 * ui.scale, rect.y + 88 * ui.scale, {
      size: 10,
      colour: maxed ? PALETTE.good : PALETTE.muted,
      maxWidth: rect.w - 24 * ui.scale,
    });

    ui.button(maxed ? 'Maxed' : `Install · ${formatMoney(price)}`, {
      x: rect.x + 12 * ui.scale,
      y: rect.y + cardH - 34 * ui.scale,
      w: rect.w - 24 * ui.scale,
      h: 26 * ui.scale,
    }, () => {
      app.upgradeIndex = i;
      app.buySelectedUpgrade();
    }, { small: true, disabled: maxed || !affordable || app.purchaseBusy, variant: maxed ? 'ghost' : 'primary' });
  });

  if (app.purchaseError) {
    // Beside the back button, not on top of it.
    ui.text(app.purchaseError, footer.x + 124 * ui.scale, footer.y + footer.h / 2, {
      size: 11,
      colour: PALETTE.bad,
      maxWidth: footer.w - 140 * ui.scale,
    });
  }
  backButton(app, footer);
}

// ------------------------------------------------------------------ tracks

export function drawTracks(app: GameApi): void {
  const ui = app.ui;
  const { content, footer } = shell(ui, 'Tracks & records');
  const gap = 14 * ui.scale;
  const listW = Math.min(content.w * 0.38, 420 * ui.scale);
  const listRect: Rect = { x: content.x, y: content.y, w: listW, h: content.h - 56 * ui.scale };
  const detailRect: Rect = { x: content.x + listW + gap, y: content.y, w: content.w - listW - gap, h: content.h - 56 * ui.scale };

  ui.panel(listRect, 'Circuits');
  const rowH = 48 * ui.scale;
  TRACKS.forEach((track, index) => {
    const y = listRect.y + 44 * ui.scale + index * rowH;
    if (y + rowH > listRect.y + listRect.h) return;
    const selected = app.trackIndex === index;
    const rect = { x: listRect.x + 10 * ui.scale, y, w: listRect.w - 20 * ui.scale, h: rowH - 4 * ui.scale };
    ui.row(rect, { selected, onClick: () => { app.trackIndex = index; app.loadLeaderboard(track.id); } });
    ui.text(track.name, rect.x + 10 * ui.scale, rect.y + 15 * ui.scale, { size: 13, weight: '700' });
    ui.text(track.environment, rect.x + 10 * ui.scale, rect.y + 32 * ui.scale, { size: 10, colour: PALETTE.muted });
    const best = app.store.save.bestLaps[track.id] ?? 0;
    ui.text(best > 0 ? formatTime(best) : '—', rect.x + rect.w - 10 * ui.scale, rect.y + 22 * ui.scale, {
      size: 11,
      colour: best > 0 ? PALETTE.good : PALETTE.muted,
      align: 'right',
    });
  });

  const track = TRACKS[clamp(app.trackIndex, 0, TRACKS.length - 1)]!;
  ui.panel(detailRect, track.name);
  const lines = ui.wrap(track.description, detailRect.w - 32 * ui.scale, 12);
  lines.slice(0, 3).forEach((line, i) => {
    ui.text(line, detailRect.x + 16 * ui.scale, detailRect.y + 56 * ui.scale + i * 17 * ui.scale, { size: 12, colour: PALETTE.muted });
  });

  const facts: Array<[string, string]> = [
    ['Environment', track.environment],
    ['Difficulty', `${track.difficulty} / 5`],
    ['Suggested laps', String(track.defaultLaps)],
    ['Road surface', track.road],
    ['Barriers', track.barriers === 'edges' ? 'Full circuit' : 'Open terrain'],
    ['Your best lap', (app.store.save.bestLaps[track.id] ?? 0) > 0 ? formatTime(app.store.save.bestLaps[track.id]!) : 'not set'],
    ['Your best race', (app.store.save.bestRaces[track.id] ?? 0) > 0 ? formatTime(app.store.save.bestRaces[track.id]!) : 'not set'],
  ];
  facts.forEach(([label, value], i) => {
    const y = detailRect.y + 130 * ui.scale + i * 22 * ui.scale;
    ui.text(label, detailRect.x + 16 * ui.scale, y, { size: 11, colour: PALETTE.muted });
    ui.text(value, detailRect.x + detailRect.w - 16 * ui.scale, y, { size: 11, align: 'right' });
  });

  const lbY = detailRect.y + 130 * ui.scale + facts.length * 22 * ui.scale + 18 * ui.scale;
  ui.text('SERVER LEADERBOARD', detailRect.x + 16 * ui.scale, lbY, { size: 10, colour: PALETTE.accent, weight: '700' });
  if (app.leaderboardLoading || app.leaderboardTrack !== track.id) {
    ui.text('Loading…', detailRect.x + 16 * ui.scale, lbY + 22 * ui.scale, { size: 11, colour: PALETTE.muted });
  } else if (app.leaderboard.length === 0) {
    ui.text('No recorded laps yet. Be the first.', detailRect.x + 16 * ui.scale, lbY + 22 * ui.scale, { size: 11, colour: PALETTE.muted });
  } else {
    app.leaderboard.slice(0, 6).forEach((entry, i) => {
      const y = lbY + 22 * ui.scale + i * 20 * ui.scale;
      ui.text(`${i + 1}. ${entry.displayName}`, detailRect.x + 16 * ui.scale, y, { size: 11, maxWidth: detailRect.w - 140 * ui.scale });
      ui.text(formatTime(entry.bestLap), detailRect.x + detailRect.w - 16 * ui.scale, y, { size: 11, align: 'right', colour: PALETTE.accent });
    });
  }

  ui.button('Race this track', {
    x: detailRect.x + 16 * ui.scale,
    y: detailRect.y + detailRect.h - 46 * ui.scale,
    w: 180 * ui.scale,
    h: 38 * ui.scale,
  }, () => {
    app.setup.trackId = track.id;
    app.show('play');
  }, { variant: 'primary' });
  backButton(app, footer);
}

// ------------------------------------------------------------------- stats

export function drawStats(app: GameApi): void {
  const ui = app.ui;
  const { content, footer } = shell(ui, 'Statistics', app.store.user?.username ?? '');
  const save = app.store.save;

  const cards: Array<[string, string]> = [
    ['Races', String(save.stats.races)],
    ['Wins', String(save.stats.wins)],
    ['Podiums', String(save.stats.podiums)],
    ['Clean laps', String(save.stats.cleanLaps)],
    ['Drift points', String(Math.round(save.stats.driftScore))],
    ['Crashes', String(save.stats.crashes)],
    ['Cars owned', `${save.cars.length} / ${CARS.length}`],
    ['Achievements', `${save.achievements.length} / ${ACHIEVEMENTS.length}`],
  ];
  const cols = 4;
  const gap = 12 * ui.scale;
  const cardW = (content.w - gap * (cols - 1)) / cols;
  cards.forEach(([label, value], i) => {
    const rect = {
      x: content.x + (i % cols) * (cardW + gap),
      y: content.y + Math.floor(i / cols) * (86 * ui.scale + gap),
      w: cardW,
      h: 86 * ui.scale,
    };
    ui.rect(rect, PALETTE.panel, PALETTE.line);
    ui.text(label.toUpperCase(), rect.x + 12 * ui.scale, rect.y + 22 * ui.scale, { size: 10, colour: PALETTE.muted });
    ui.text(value, rect.x + 12 * ui.scale, rect.y + 50 * ui.scale, { size: 22, weight: '700', colour: PALETTE.accent });
  });

  const achY = content.y + 2 * (86 * ui.scale + gap) + 12 * ui.scale;
  ui.panel({ x: content.x, y: achY, w: content.w, h: content.h - (achY - content.y) - 56 * ui.scale }, 'Achievements');
  const perRow = Math.floor((content.w - 24 * ui.scale) / (230 * ui.scale));
  ACHIEVEMENTS.forEach((ach, i) => {
    const col = i % perRow;
    const row = Math.floor(i / perRow);
    const rect = {
      x: content.x + 12 * ui.scale + col * 230 * ui.scale,
      y: achY + 46 * ui.scale + row * 38 * ui.scale,
      w: 220 * ui.scale,
      h: 32 * ui.scale,
    };
    const unlocked = save.achievements.includes(ach.id);
    ui.rect(rect, unlocked ? '#1d2a22' : PALETTE.panelAlt, unlocked ? PALETTE.good : PALETTE.line);
    ui.text(ach.name, rect.x + 10 * ui.scale, rect.y + 16 * ui.scale, {
      size: 12,
      weight: '700',
      colour: unlocked ? PALETTE.good : PALETTE.muted,
    });
    ui.text(ach.description, rect.x + 10 * ui.scale, rect.y + 27 * ui.scale, { size: 9, colour: PALETTE.muted, maxWidth: rect.w - 20 * ui.scale });
  });

  backButton(app, footer);
}

// ---------------------------------------------------------------- settings

export function drawSettings(app: GameApi): void {
  const ui = app.ui;
  const { content, footer } = shell(ui, 'Settings');
  const tabs = ['Audio', 'Graphics', 'Controls', 'Account'];
  const tabRect = { x: content.x, y: content.y, w: content.w, h: 36 * ui.scale };
  const tab = ui.tabs(tabs, tabRect, app.settingsTab);
  if (tab !== app.settingsTab) app.settingsTab = tab;

  const panel: Rect = { x: content.x, y: content.y + 48 * ui.scale, w: content.w, h: content.h - 48 * ui.scale - 56 * ui.scale };
  ui.panel(panel, tabs[app.settingsTab]);
  const sx = panel.x + 20 * ui.scale;
  const sw = Math.min(panel.w - 40 * ui.scale, 560 * ui.scale);
  let y = panel.y + 58 * ui.scale;
  const rowH = 42 * ui.scale;

  const settings = app.store.settings;
  /** Writes one setting and immediately re-applies it to the live systems. */
  const patch = <K extends keyof GameSettings>(key: K, value: GameSettings[K]): void => {
    if (settings[key] === value) return;
    app.store.updateSettings({ [key]: value });
    app.applySettings();
  };

  if (app.settingsTab === 0) {
    patch('masterVolume', ui.slider('Master volume', { x: sx, y, w: sw, h: rowH - 6 }, settings.masterVolume, 0, 1, 0.05, (v) => `${Math.round(v * 100)}%`)); y += rowH;
    patch('musicVolume', ui.slider('Music volume', { x: sx, y, w: sw, h: rowH - 6 }, settings.musicVolume, 0, 1, 0.05, (v) => `${Math.round(v * 100)}%`)); y += rowH;
    patch('sfxVolume', ui.slider('Effects volume', { x: sx, y, w: sw, h: rowH - 6 }, settings.sfxVolume, 0, 1, 0.05, (v) => `${Math.round(v * 100)}%`)); y += rowH;
    ui.text('Engine audio, tyre squeal and surface noise are all synthesised live.', sx, y + 8 * ui.scale, { size: 11, colour: PALETTE.muted });
  } else if (app.settingsTab === 1) {
    patch('pixelScale', ui.slider('Pixel scale', { x: sx, y, w: sw, h: rowH - 6 }, settings.pixelScale, 1, 6, 1, (v) => `${Math.round(v)}×`)); y += rowH;
    patch('cameraZoom', ui.slider('Camera zoom', { x: sx, y, w: sw, h: rowH - 6 }, settings.cameraZoom, 0.5, 1.6, 0.05, (v) => `${v.toFixed(2)}×`)); y += rowH;
    patch('screenShake', ui.toggle('Screen shake', { x: sx, y, w: sw, h: rowH - 6 }, settings.screenShake)); y += rowH;
    patch('reducedMotion', ui.toggle('Reduced motion', { x: sx, y, w: sw, h: rowH - 6 }, settings.reducedMotion)); y += rowH;
    patch('showRacingLine', ui.toggle('Racing line assist', { x: sx, y, w: sw, h: rowH - 6 }, settings.showRacingLine)); y += rowH;
    patch('minimap', ui.toggle('Minimap', { x: sx, y, w: sw, h: rowH - 6 }, settings.minimap)); y += rowH;
    patch('autoAccelerate', ui.toggle('Auto accelerate', { x: sx, y, w: sw, h: rowH - 6 }, settings.autoAccelerate)); y += rowH;
    patch('units', ui.toggle('Imperial units', { x: sx, y, w: sw, h: rowH - 6 }, settings.units === 'imperial') ? 'imperial' : 'metric'); y += rowH;
    ui.text('Settings persist on the server with the rest of your save.', sx, y + 4 * ui.scale, { size: 11, colour: PALETTE.muted });
  } else if (app.settingsTab === 2) {
    const controls = app.store.controls;
    const keys: Array<[keyof typeof controls, string]> = [
      ['up', 'Accelerate'],
      ['down', 'Brake / reverse'],
      ['left', 'Steer left'],
      ['right', 'Steer right'],
      ['handbrake', 'Handbrake'],
      ['boost', 'Turbo'],
      ['reset', 'Recover car'],
      ['camera', 'Cycle camera'],
    ];
    keys.forEach(([key, label]) => {
      ui.text(label, sx, y + 16 * ui.scale, { size: 12 });
      const rect = { x: sx + sw - 160 * ui.scale, y, w: 160 * ui.scale, h: rowH - 8 };
      const listening = app.rebindTarget === key;
      ui.button(listening ? 'Press a key…' : prettyKey(controls[key]), rect, () => {
        app.rebindTarget = listening ? null : key;
      }, { small: true, variant: listening ? 'primary' : 'subtle' });
      y += rowH;
    });
    const sensitivityY = y + 8 * ui.scale;
    const value = app.steeringSensitivity;
    const next = ui.slider('Steering sensitivity', { x: sx, y: sensitivityY, w: sw, h: rowH - 6 }, value, 0.4, 2, 0.05, (v) => `${v.toFixed(2)}×`);
    if (next !== value) app.setSteeringSensitivity(next);
    ui.text('Keyboard and on-screen touch controls only.', sx, sensitivityY + 34 * ui.scale, {
      size: 11,
      colour: PALETTE.muted,
    });
  } else {
    const user = app.store.user;
    if (user) {
      ui.text(`Signed in as ${user.displayName} (${user.username})`, sx, y, { size: 13, weight: '700' });
      y += 24 * ui.scale;
      ui.text(user.isAdmin ? 'Administrator account' : 'Player account', sx, y, { size: 11, colour: PALETTE.muted });
      y += 28 * ui.scale;
      ui.text(`Member since ${new Date(user.createdAt).toLocaleDateString()}`, sx, y, { size: 11, colour: PALETTE.muted });
      y += 32 * ui.scale;
      ui.button('Open admin console', { x: sx, y, w: 200 * ui.scale, h: 36 * ui.scale }, () => {
        window.open('/admin/', '_blank', 'noopener');
      }, { disabled: !user.isAdmin });
      y += 46 * ui.scale;
      ui.button('Sign out', { x: sx, y, w: 140 * ui.scale, h: 36 * ui.scale }, () => app.signOut(), { variant: 'danger' });
      y += 52 * ui.scale;
      ui.text('Password changes are handled from the admin console or the CLI.', sx, y, { size: 11, colour: PALETTE.muted });
    } else {
      ui.text('Not signed in.', sx, y, { size: 13, colour: PALETTE.warn });
    }
  }

  backButton(app, footer);
}

// ------------------------------------------------------------- multiplayer

export function drawMultiplayer(app: GameApi): void {
  const ui = app.ui;
  const net = app.net;
  const { content, footer } = shell(ui, 'Multiplayer', net.connected ? `connected · ${net.ping}ms` : net.state);

  if (!net.connected) {
    ui.panel(content, 'Connecting');
    ui.text(
      net.state === 'error' ? net.error || 'Could not reach the race server.' : 'Opening a connection to the race server…',
      content.x + 20 * ui.scale,
      content.y + 70 * ui.scale,
      { size: 13, colour: PALETTE.warn },
    );
    ui.button('Retry', { x: content.x + 20 * ui.scale, y: content.y + 110 * ui.scale, w: 120 * ui.scale, h: 38 * ui.scale }, () => {
      net.disconnect();
      net.connect();
    }, { variant: 'primary' });
    backButton(app, footer);
    return;
  }

  const gap = 14 * ui.scale;
  const leftW = Math.min(content.w * 0.55, 620 * ui.scale);
  const listRect: Rect = { x: content.x, y: content.y, w: leftW, h: content.h - 56 * ui.scale };
  const rightRect: Rect = { x: content.x + leftW + gap, y: content.y, w: content.w - leftW - gap, h: content.h - 56 * ui.scale };

  ui.panel(listRect, 'Public lobbies');
  ui.button('Refresh list', { x: listRect.x + listRect.w - 130 * ui.scale, y: listRect.y + 6 * ui.scale, w: 118 * ui.scale, h: 26 * ui.scale }, () => {
    net.send({ t: 'listLobbies' });
  }, { small: true });

  const visible = net.lobbies;
  if (visible.length === 0) {
    ui.text('No open lobbies right now.', listRect.x + 16 * ui.scale, listRect.y + 70 * ui.scale, { size: 12, colour: PALETTE.muted });
    ui.text('Create one on the right — private rooms only appear to their members.', listRect.x + 16 * ui.scale, listRect.y + 90 * ui.scale, {
      size: 11,
      colour: PALETTE.muted,
    });
  }
  const rowH = 46 * ui.scale;
  visible.slice(0, 9).forEach((info, i) => {
    const rect = { x: listRect.x + 12 * ui.scale, y: listRect.y + 44 * ui.scale + i * rowH, w: listRect.w - 24 * ui.scale, h: rowH - 4 * ui.scale };
    ui.row(rect, {
      disabled: info.inProgress,
      onClick: () => {
        app.joinLobby(info.code);
      },
    });
    ui.text(info.name, rect.x + 10 * ui.scale, rect.y + 14 * ui.scale, { size: 12, weight: '700', maxWidth: rect.w - 200 * ui.scale });
    ui.text(`${TRACK_BY_ID[info.trackId]?.name ?? info.trackId} · ${info.laps} laps`, rect.x + 10 * ui.scale, rect.y + 30 * ui.scale, {
      size: 10,
      colour: PALETTE.muted,
    });
    ui.text(`${info.playerCount}/${info.maxPlayers}`, rect.x + rect.w - 92 * ui.scale, rect.y + 22 * ui.scale, { size: 12, align: 'right' });
    ui.text(info.inProgress ? 'racing' : info.code, rect.x + rect.w - 10 * ui.scale, rect.y + 22 * ui.scale, {
      size: 11,
      align: 'right',
      colour: info.inProgress ? PALETTE.warn : PALETTE.accent,
    });
  });

  // ------------------------------------------------------------ create / join
  ui.panel(rightRect, 'Create or join');
  const rx = rightRect.x + 16 * ui.scale;
  let ry = rightRect.y + 52 * ui.scale;
  const rw = rightRect.w - 32 * ui.scale;

  ui.text('Room name', rx, ry, { size: 10, colour: PALETTE.muted });
  ry += 22 * ui.scale;
  app.lobbyDraft.isPrivate = ui.toggle('Private room', { x: rx, y: ry, w: rw, h: 30 * ui.scale }, app.lobbyDraft.isPrivate);
  ry += 38 * ui.scale;
  app.lobbyDraft.maxPlayers = ui.slider('Max players', { x: rx, y: ry, w: rw, h: 30 * ui.scale }, app.lobbyDraft.maxPlayers, 2, 8, 1, (v) => String(Math.round(v)));
  ry += 36 * ui.scale;
  app.lobbyDraft.aiCount = ui.slider('AI opponents', { x: rx, y: ry, w: rw, h: 30 * ui.scale }, app.lobbyDraft.aiCount, 0, 8, 1, (v) => String(Math.round(v)));
  ry += 36 * ui.scale;
  app.lobbyDraft.laps = ui.slider('Laps', { x: rx, y: ry, w: rw, h: 30 * ui.scale }, app.lobbyDraft.laps, 1, 20, 1, (v) => String(Math.round(v)));
  ry += 42 * ui.scale;
  ui.button('Create lobby', { x: rx, y: ry, w: rw, h: 38 * ui.scale }, () => app.createLobby(), {
    variant: 'primary',
    disabled: app.mpBusy,
  });
  ry += 50 * ui.scale;

  ui.divider(rx, ry, rx + rw);
  ry += 18 * ui.scale;
  ui.text('JOIN BY CODE', rx, ry, { size: 10, colour: PALETTE.muted });
  ry += 22 * ui.scale;
  const codeRect = { x: rx, y: ry, w: rw, h: 38 * ui.scale };
  if (app.lobbyCodeFocus) {
    app.text.position(codeRect);
  } else {
    ui.rect(codeRect, PALETTE.panelAlt, PALETTE.line);
    ui.text(app.lobbyCodeEntry || 'ABC12', codeRect.x + 12, codeRect.y + codeRect.h / 2, {
      size: 15,
      colour: app.lobbyCodeEntry ? PALETTE.ink : '#5b6172',
    });
    const hovered = ui.pointerIn(codeRect);
    if (hovered && ui.pointer.pressed) {
      app.lobbyCodeFocus = true;
      app.text.focus(codeRect, {
        value: app.lobbyCodeEntry,
        placeholder: 'ABC12',
        maxLength: 8,
        onChange: (value) => {
          app.lobbyCodeEntry = value.toUpperCase();
        },
        onSubmit: (value) => {
          app.lobbyCodeFocus = false;
          app.joinLobby(value);
        },
      });
    }
  }
  ry += 46 * ui.scale;
  ui.button('Join by code', { x: rx, y: ry, w: rw, h: 34 * ui.scale }, () => {
    app.lobbyCodeFocus = false;
    app.text.blur();
    app.joinLobby(app.lobbyCodeEntry);
  }, { disabled: app.mpBusy || app.lobbyCodeEntry.length < 3 });

  if (net.error) {
    ui.text(net.error, rx, rightRect.y + rightRect.h - 40 * ui.scale, { size: 11, colour: PALETTE.bad, maxWidth: rw });
  }

  backButton(app, footer);
}

export function drawLobby(app: GameApi): void {
  const ui = app.ui;
  const net = app.net;
  const lobby = net.lobby;
  if (!lobby) {
    app.show('multiplayer');
    return;
  }
  const { content, footer } = shell(ui, `Lobby ${lobby.code}`, lobby.name);
  const isHost = net.isHost();

  const gap = 14 * ui.scale;
  const membersRect: Rect = { x: content.x, y: content.y, w: Math.min(content.w * 0.5, 560 * ui.scale), h: content.h - 56 * ui.scale };
  const settingsRect: Rect = {
    x: content.x + membersRect.w + gap,
    y: content.y,
    w: content.w - membersRect.w - gap,
    h: content.h - 56 * ui.scale,
  };

  ui.panel(membersRect, `Grid (${lobby.members.length})`);
  const rowH = 40 * ui.scale;
  lobby.members.forEach((member, i) => {
    const rect = { x: membersRect.x + 12 * ui.scale, y: membersRect.y + 44 * ui.scale + i * rowH, w: membersRect.w - 24 * ui.scale, h: rowH - 4 * ui.scale };
    if (rect.y + rect.h > membersRect.y + membersRect.h) return;
    ui.rect(rect, member.id === net.playerId ? '#2a3040' : PALETTE.panel, PALETTE.line);
    ui.ctx.fillStyle = member.color;
    ui.ctx.fillRect(rect.x + 10 * ui.scale, rect.y + rect.h / 2 - 6 * ui.scale, 12 * ui.scale, 12 * ui.scale);
    ui.text(member.name, rect.x + 32 * ui.scale, rect.y + rect.h / 2, { size: 12, weight: member.id === net.playerId ? '700' : '400' });
    ui.text(CAR_BY_ID[member.carId]?.name ?? member.carId, rect.x + rect.w - 130 * ui.scale, rect.y + rect.h / 2, {
      size: 11,
      colour: PALETTE.muted,
      align: 'right',
      maxWidth: 110 * ui.scale,
    });
    const status = member.isHost ? 'HOST' : member.isAI ? 'AI' : member.ready ? 'READY' : 'waiting';
    ui.text(status, rect.x + rect.w - 10 * ui.scale, rect.y + rect.h / 2, {
      size: 10,
      align: 'right',
      colour: member.isHost ? PALETTE.accent : member.isAI ? PALETTE.muted : member.ready ? PALETTE.good : PALETTE.warn,
    });
  });

  // -------------------------------------------------------------- settings
  ui.panel(settingsRect, 'Race settings');
  const scale = ui.scale;
  const sx = settingsRect.x + 16 * scale;
  const sw = settingsRect.w - 32 * scale;
  let sy = settingsRect.y + 52 * scale;
  const settings = lobby.settings;
  /** Non-host human drivers who have not readied up yet. */
  const needsReady = lobby.members.some((m) => !m.isAI && !m.ready && m.id !== lobby.hostId);

  const change = (patch: Partial<typeof settings>): void => {
    net.updateSettings(patch);
  };

  ui.text('Circuit', sx, sy, { size: 10, colour: PALETTE.muted });
  sy += 16 * scale;
  // Two columns so all seven circuits fit the column without spilling onto the
  // controls below — and `sy` is advanced by the number of *rows actually
  // drawn*, which is what stops the Laps slider landing on top of the list.
  const trackCols = sw > 300 * scale ? 2 : 1;
  const trackRowH = 24 * scale;
  const trackCellW = (sw - (trackCols - 1) * 6 * scale) / trackCols;
  const listBottom = settingsRect.y + settingsRect.h - (needsReady ? 132 : 90) * scale;
  let rowsDrawn = 0;
  TRACKS.forEach((track, i) => {
    const col = i % trackCols;
    const row = Math.floor(i / trackCols);
    const rect = { x: sx + col * (trackCellW + 6 * scale), y: sy + row * trackRowH, w: trackCellW, h: trackRowH - 3 * scale };
    if (rect.y + rect.h > listBottom) return;
    ui.row(rect, { selected: settings.trackId === track.id, disabled: !isHost, onClick: () => change({ trackId: track.id }) });
    ui.fitText(track.name, rect.x + 8 * scale, rect.y + rect.h / 2, rect.w - 16 * scale, { size: 11, colour: PALETTE.muted });
    rowsDrawn = Math.max(rowsDrawn, row + 1);
  });
  sy += rowsDrawn * trackRowH + 10 * scale;

  const laps = ui.slider('Laps', { x: sx, y: sy, w: sw, h: 30 * scale }, settings.laps, 1, 20, 1, (v) => String(Math.round(v)));
  if (isHost && laps !== settings.laps) change({ laps });
  sy += 34 * scale;
  const aiCount = ui.slider('AI', { x: sx, y: sy, w: sw, h: 30 * scale }, settings.aiCount, 0, 8, 1, (v) => String(Math.round(v)));
  if (isHost && aiCount !== settings.aiCount) change({ aiCount });
  sy += 40 * scale;

  // The primary action sits directly under the last control; "Ready up" (when
  // present) is pinned to the bottom of the panel, so the two can never collide
  // however tall the circuit list turns out to be.
  ui.button(isHost ? 'Start race' : 'Waiting for host…', { x: sx, y: sy, w: sw, h: 38 * scale }, () => app.startOnlineRace(), {
    variant: 'primary',
    disabled: !isHost || lobby.members.filter((m) => !m.isAI).length < 1,
  });
  const bottom = settingsRect.y + settingsRect.h;
  const readyY = bottom - 42 * scale;
  const leaveY = needsReady ? readyY - 42 * scale : readyY;

  ui.button('Leave lobby', { x: sx, y: leaveY, w: sw, h: 34 * scale }, () => app.leaveLobby(), { variant: 'danger' });

  if (needsReady) {
    ui.button('Ready up', { x: sx, y: readyY, w: sw, h: 34 * scale }, () => {
      const me = lobby.members.find((m) => m.id === net.playerId);
      net.setReady(!(me?.ready ?? false));
    });
  }

  backButton(app, footer);
}

// ----------------------------------------------------------------- results

export function drawResults(app: GameApi): void {
  const ui = app.ui;
  const results = app.results;
  const { content, footer } = shell(ui, 'Race results', results ? `${TRACK_BY_ID[results.trackId]?.name ?? ''}` : '');
  if (!results) {
    backButton(app, footer);
    return;
  }

  const gap = 14 * ui.scale;
  const tableRect: Rect = { x: content.x, y: content.y, w: Math.min(content.w * 0.56, 640 * ui.scale), h: content.h - 56 * ui.scale };
  const rewardRect: Rect = {
    x: content.x + tableRect.w + gap,
    y: content.y,
    w: content.w - tableRect.w - gap,
    h: content.h - 56 * ui.scale,
  };

  ui.panel(tableRect, 'Final standings');
  const rowH = 32 * ui.scale;
  results.results.forEach((row, i) => {
    const rect = { x: tableRect.x + 12 * ui.scale, y: tableRect.y + 44 * ui.scale + i * rowH, w: tableRect.w - 24 * ui.scale, h: rowH - 3 * ui.scale };
    if (rect.y + rect.h > tableRect.y + tableRect.h - 8 * ui.scale) return;
    const isPlayer = row.id === results.playerResult.id;
    ui.rect(rect, isPlayer ? '#2a3040' : PALETTE.panel, isPlayer ? PALETTE.accent : PALETTE.line);
    const medal = row.position === 1 ? PALETTE.accent : row.position === 2 ? '#c8ccd4' : row.position === 3 ? '#c98a4b' : PALETTE.muted;
    ui.text(String(row.position), rect.x + 16 * ui.scale, rect.y + rect.h / 2, { size: 14, weight: '700', colour: medal });
    ui.text(row.name, rect.x + 40 * ui.scale, rect.y + rect.h / 2, { size: 12, maxWidth: rect.w - 200 * ui.scale });
    ui.text(row.finished ? formatTime(row.time) : 'DNF', rect.x + rect.w - 96 * ui.scale, rect.y + rect.h / 2, { size: 12, align: 'right' });
    ui.text(row.bestLap > 0 ? formatTime(row.bestLap) : '—', rect.x + rect.w - 10 * ui.scale, rect.y + rect.h / 2, {
      size: 11,
      align: 'right',
      colour: PALETTE.muted,
    });
  });

  // -------------------------------------------------------------- rewards
  ui.panel(rewardRect, 'Payout');
  const rx = rewardRect.x + 16 * ui.scale;
  let ry = rewardRect.y + 54 * ui.scale;
  const rewards = results.serverRewards ?? results.rewards;

  ui.text(results.serverRewards ? 'Confirmed by the server' : 'Provisional — server unavailable', rx, ry, {
    size: 10,
    colour: results.serverRewards ? PALETTE.good : PALETTE.warn,
  });
  ry += 26 * ui.scale;
  ui.text(`+${formatMoney(rewards.money)}`, rx, ry, { size: 28, weight: '700', colour: PALETTE.accent });
  ry += 34 * ui.scale;
  ui.text(`${rewards.xp} XP earned`, rx, ry, { size: 13, colour: PALETTE.muted });
  ry += 26 * ui.scale;

  const lines: Array<[string, number]> = [
    ['Base', rewards.base],
    ['Position', rewards.positionBonus],
    ['Laps', rewards.lapBonus],
    ['Drift', rewards.driftBonus],
    ['Opponents', rewards.difficultyBonus],
    ['Best lap', rewards.bestLapBonus],
  ];
  for (const [label, value] of lines) {
    if (value === 0) continue;
    ui.text(label, rx, ry, { size: 11, colour: PALETTE.muted });
    ui.text(`+${formatMoney(value)}`, rx + rewardRect.w - 32 * ui.scale, ry, { size: 11, align: 'right' });
    ry += 19 * ui.scale;
  }

  if (results.personalBest) {
    ry += 6 * ui.scale;
    ui.text('NEW PERSONAL BEST LAP', rx, ry, { size: 12, weight: '700', colour: PALETTE.good });
    ry += 20 * ui.scale;
  }

  results.newAchievements.forEach((ach) => {
    ui.text(`★ ${ach.name} (+${formatMoney(ach.reward)})`, rx, ry, { size: 11, colour: PALETTE.accent });
    ry += 18 * ui.scale;
  });

  if (results.level) {
    ry = rewardRect.y + rewardRect.h - 66 * ui.scale;
    ui.text(`Level ${results.level.level}`, rx, ry, { size: 12, weight: '700' });
    ui.bar(
      { x: rx, y: ry + 12 * ui.scale, w: rewardRect.w - 32 * ui.scale, h: 8 * ui.scale },
      results.level.needed > 0 ? results.level.intoLevel / results.level.needed : 0,
    );
    ui.text(`${results.level.intoLevel} / ${results.level.needed} XP to next level`, rx, ry + 30 * ui.scale, {
      size: 10,
      colour: PALETTE.muted,
    });
  }

  if (results.submitError) {
    ui.text(results.submitError, rx, rewardRect.y + rewardRect.h - 22 * ui.scale, { size: 10, colour: PALETTE.bad, maxWidth: rewardRect.w - 32 * ui.scale });
  }

  // The action row shares the footer band with every other screen's buttons, so
  // it is marked pinned: those rects must not be scrolled or clipped away.
  footerBar(ui, footer);
  const btnH = Math.min(42 * ui.scale, footer.h);
  const buttonY = footer.y + (footer.h - btnH) / 2;
  ui.button('Race again', { x: footer.x, y: buttonY, w: 160 * ui.scale, h: btnH }, () => app.restartRace(), {
    variant: 'primary',
    pinned: true,
  });
  ui.button('Garage', { x: footer.x + 172 * ui.scale, y: buttonY, w: 130 * ui.scale, h: btnH }, () => {
    app.results = null;
    app.openGarage();
  }, { pinned: true });
  ui.button('Main menu', { x: footer.x + 314 * ui.scale, y: buttonY, w: 150 * ui.scale, h: btnH }, () => {
    app.results = null;
    app.show('menu');
  }, { pinned: true });
}

// ------------------------------------------------------------------- misc

export function drawPauseOverlay(app: GameApi): void {
  const ui = app.ui;
  const ctx = ui.ctx;
  ctx.fillStyle = 'rgba(6,7,10,0.72)';
  ctx.fillRect(0, 0, ui.width, ui.height);

  const w = Math.min(420 * ui.scale, ui.width - 40);
  const h = 330 * ui.scale;
  const rect: Rect = { x: (ui.width - w) / 2, y: (ui.height - h) / 2, w, h };
  ui.rect(rect, PALETTE.panel, PALETTE.line);
  ui.rect({ x: rect.x, y: rect.y, w: rect.w, h: 4 }, PALETTE.accent);
  ui.text('PAUSED', rect.x + rect.w / 2, rect.y + 40 * ui.scale, { size: 24, weight: '700', align: 'center' });

  const bx = rect.x + 24 * ui.scale;
  const bw = rect.w - 48 * ui.scale;
  const items: Array<[string, () => void, 'primary' | 'subtle' | 'danger']> = [
    ['Resume', () => app.show('race'), 'primary'],
    ['Restart race', () => app.restartRace(), 'subtle'],
    ['Settings', () => app.show('settings'), 'subtle'],
    ['Quit to menu', () => app.quitRace(), 'danger'],
  ];
  items.forEach(([label, action, variant], i) => {
    ui.button(label, { x: bx, y: rect.y + 76 * ui.scale + i * 52 * ui.scale, w: bw, h: 44 * ui.scale }, action, { variant });
  });
  ui.text('Esc resumes · R recovers your car', rect.x + rect.w / 2, rect.y + h - 22 * ui.scale, {
    size: 10,
    colour: PALETTE.muted,
    align: 'center',
  });
}

export function drawToasts(app: GameApi): void {
  const ui = app.ui;
  const now = Date.now();
  const visible = app.toasts.filter((t) => now - t.at < 4200);
  visible.forEach((toast, i) => {
    const y = ui.height - 90 * ui.scale - i * 40 * ui.scale;
    const width = Math.min(ui.width - 40, 460 * ui.scale);
    const x = (ui.width - width) / 2;
    const age = (now - toast.at) / 4200;
    ui.ctx.globalAlpha = clamp(1 - Math.max(0, age - 0.8) / 0.2, 0, 1);
    ui.rect({ x, y, w: width, h: 34 * ui.scale }, 'rgba(23,26,34,0.96)', toast.bad ? PALETTE.bad : PALETTE.accent);
    ui.text(toast.message, x + 12 * ui.scale, y + 17 * ui.scale, {
      size: 12,
      colour: toast.bad ? '#ffb4ac' : PALETTE.ink,
      maxWidth: width - 24 * ui.scale,
    });
    ui.ctx.globalAlpha = 1;
  });
}

export function drawConfirm(app: GameApi): void {
  const dialog = app.confirm;
  if (!dialog) return;
  const ui = app.ui;
  const ctx = ui.ctx;
  ctx.fillStyle = 'rgba(6,7,10,0.7)';
  ctx.fillRect(0, 0, ui.width, ui.height);

  const w = Math.min(440 * ui.scale, ui.width - 40);
  const h = 200 * ui.scale;
  const rect: Rect = { x: (ui.width - w) / 2, y: (ui.height - h) / 2, w, h };
  ui.rect(rect, PALETTE.panel, PALETTE.line);
  ui.rect({ x: rect.x, y: rect.y, w: rect.w, h: 4 }, PALETTE.bad);
  ui.text(dialog.title, rect.x + 20 * ui.scale, rect.y + 40 * ui.scale, { size: 17, weight: '700' });
  const lines = ui.wrap(dialog.body, rect.w - 40 * ui.scale, 12);
  lines.slice(0, 4).forEach((line, i) => {
    ui.text(line, rect.x + 20 * ui.scale, rect.y + 68 * ui.scale + i * 17 * ui.scale, { size: 12, colour: PALETTE.muted });
  });

  const buttonY = rect.y + h - 56 * ui.scale;
  ui.button(dialog.confirmLabel, { x: rect.x + 20 * ui.scale, y: buttonY, w: 150 * ui.scale, h: 40 * ui.scale }, () => {
    dialog.onConfirm();
    app.confirm = null;
  }, { variant: 'danger' });
  ui.button('Cancel', { x: rect.x + 182 * ui.scale, y: buttonY, w: 120 * ui.scale, h: 40 * ui.scale }, () => {
    app.confirm = null;
  });
}

function prettyKey(code: string): string {
  return code
    .replace('Key', '')
    .replace('Arrow', '')
    .replace('Digit', '')
    .replace('Left', ' L')
    .replace('Right', ' R')
    .toUpperCase();
}
